import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import styles from './style.module.scss'
import { EmptyData } from 'utils/ui'
import AgendaCell from '../AgendaCell'

interface CalendarEvent {
  id: string
  startDate: Date
  endDate: Date
  startTime: Date
  subject: string
  location?: string
  color?: string
  allday?: boolean
  attendees?: any[]
  isPrivate?: boolean
  isRecurring?: boolean
  hasException?: boolean
  todo?: any
}

interface GroupedEvent {
  date: string
  events: CalendarEvent[]
  isLastDate: boolean
}

interface AgendaBoxProps {
  events?: CalendarEvent[]
  className?: string
  onEventClick: (event: CalendarEvent, date: Date, ref?: React.RefObject<HTMLDivElement>) => void
  onFocusedDate?: (date: Date) => void
}

const AgendaBox: React.FC<AgendaBoxProps> = ({
  events = [],
  className,
  onEventClick,
  onFocusedDate,
}) => {
  const assignedFocusDate = useRef<Date | null>(null)
  const focusRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const [groupedEvents, setGroupedEvents] = useState<GroupedEvent[]>([])

  useEffect(() => {
    assignedFocusDate.current = null
    focusRef.current = null
    setGroupedEvents(groupEventsByDate(events))
  }, [events])

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
    }
  }, [focusRef.current])

  useEffect(() => {
    if (onFocusedDate && assignedFocusDate.current) {
      onFocusedDate(assignedFocusDate.current)
    }
  }, [events, focusRef])

  const groupEventsByDate = (events: CalendarEvent[]): GroupedEvent[] => {
    const aGroupedEvents: Record<string, CalendarEvent[]> = {}

    events.forEach((event) => {
      let loopDate = new Date(
        event.startDate.getFullYear(),
        event.startDate.getMonth(),
        event.startDate.getDate(),
        0,
        0,
        0,
        0,
      )

      do {
        const date = moment(loopDate).format('YYYY-MM-DD')
        if (!aGroupedEvents[date]) {
          aGroupedEvents[date] = []
        }
        aGroupedEvents[date].push(event)
        loopDate = moment(loopDate).add(1, 'day').toDate()
      } while (loopDate < event.endDate)
    })

    return Object.entries(aGroupedEvents)
      .sort(([dateA], [dateB]) => moment(dateA).diff(moment(dateB)))
      .map(([date, events], index, array) => ({
        date,
        events: events.sort((a, b) => moment(a.startTime).diff(moment(b.startTime))),
        isLastDate: index === array.length - 1,
      }))
  }

  const renderDayGroup = (group: GroupedEvent) => {
    const date = moment(group.date)
    const isToday = date.isSame(moment(), 'day')
    const isAfterToday = date.isAfter(moment(), 'day')
    const isYesterday = date.isSame(moment().subtract(1, 'day'), 'day')
    const isTomorrow = date.isSame(moment().add(1, 'day'), 'day')
    const isSameYear = date.isSame(moment(), 'year')

    let appendStr = ''
    let assignRef = false
    if (isToday) {
      appendStr = '· ' + t('email.date.today')
      if (!assignedFocusDate.current) {
        assignedFocusDate.current = date.toDate()
        assignRef = true
      }
    } else if (isYesterday) {
      appendStr = '· ' + t('email.date.yesterday')
    } else if (isTomorrow) {
      appendStr = '· ' + t('email.date.tomorrow')
    }

    if (!assignedFocusDate.current && (isAfterToday || group.isLastDate)) {
      assignRef = true
      assignedFocusDate.current = date.toDate()
    }

    appendStr += '· ' + date.format('MMM')

    if (!isSameYear) appendStr += '· ' + date.format('YYYY')

    return (
      <div key={group.date} className={styles.dayGroup} ref={assignRef ? focusRef : null}>
        <div className={styles.dayHeader}>
          <div className={styles.date}>{date.format('D')}</div>
          <div className={styles.weekday}>{date.format('ddd')}</div>
          <div>{appendStr}</div>
        </div>
        {group.events.map((event) => (
          <AgendaCell
            key={event.id}
            event={event}
            date={date.toDate()}
            onEventClick={(event, date) => {
              ;(onEventClick as any)(event, date)
              assignedFocusDate.current = date
            }}
          />
        ))}
      </div>
    )
  }

  if (events.length === 0) {
    assignedFocusDate.current = new Date()
    return (
      <div className={styles.agendaBox}>
        <EmptyData type="search" mailbox={null} />
      </div>
    )
  }

  return (
    <div className={classNames(styles.agendaBox, className)}>
      {groupedEvents.map(renderDayGroup)}
    </div>
  )
}

export default AgendaBox
